/**
 * «Обліковий запис» → «Мої доступи» (ч.1075 → ч.1406 → ч.1418, рішення власника).
 *
 * Три однакові блоки, по одному на режим: що це за режим і що у людини в ньому є.
 * - «ЕДО» — картки учасника електронних документів у ВЛАСНИХ акаунтах (план `EDO`);
 * - «Користувач» — ЧУЖІ акаунти, куди запросили: назва, роль, доступні організації і ВЛАСНИЙ ярлик
 *   акаунта (показується у фільтрі акаунтів у шапці замість назви — щоб не плутати клієнтів зі
 *   схожими назвами; бачите лише ви). Ярлик — лише для чужих акаунтів;
 * - «Бізнес» — власні організації з обліком.
 * Значки ті самі, що в перемикачі режиму в шапці; поточний режим виділено. Біля організації — ЄДРПОУ
 * чи РНОКПП, а не внутрішній код.
 *
 * Організації акаунта, куди зараз не пускає політика власника (обовʼязковий двофакторний вхід /
 * довірені мережі), не показуємо — як і в шапці.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getMyOrganizations, getMyTenants, setTenantLabel, type AccountMode, type MyOrganizationItem,
  type MyTenantItem,
} from '@/api/auth';
import { tenantDisplayName } from '@/auth/tenantLabel';
import { useAuth } from '@/auth/store';
import { useSwitchTenant } from '@/auth/useSwitchTenant';
import { MODE_ICONS } from '@/components/ModeSwitch';
import { useToast } from '@/components/ToastProvider';
import {
  MODE_LABEL, modeAvailable, useEffectiveMode, useModeContext, useSetAccountMode,
} from '@/hooks/useAccountMode';
import { extractApiError } from '@/utils/extractApiError';

const BLOCK_TEXT: Record<string, string> = {
  TWO_FACTOR_REQUIRED: 'власник акаунта вимагає двофакторний вхід',
  IP_NOT_ALLOWED: 'власник акаунта дозволив вхід лише з довірених мереж',
};
const ENTITY_LABEL: Record<string, string> = { LEGAL_ENTITY: 'юрособа', FOP: 'ФОП', INDIVIDUAL: 'фізособа' };
const LOADING = <div className="access-note">Завантаження…</div>;

function Blocked({ reason }: { reason: string }) {
  return (
    <div className="access-note access-note--warn">
      🔒 Вхід обмежено: {BLOCK_TEXT[reason] ?? 'політика власника акаунта'}.
      Організації приховано, доки обмеження діє для вас.
    </div>
  );
}

function OrgRows({ orgs, accountOf }: { orgs: MyOrganizationItem[]; accountOf?: (id: string) => string }) {
  return (
    <ul className="access-rows">
      {orgs.map((o) => (
        <li key={o.id}>
          <div className="access-rows__name">{o.name}</div>
          <div className="access-rows__meta">
            {[o.tax_code, ENTITY_LABEL[o.entity_type ?? ''], accountOf && `акаунт «${accountOf(o.tenant_id)}»`]
              .filter(Boolean).join(' · ')}
          </div>
        </li>
      ))}
    </ul>
  );
}

/** Своя назва чужого акаунта: поле відкривається на вимогу, щоб не заступати перелік. */
function LabelEditor({ tenant, onClose }: { tenant: MyTenantItem; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [value, setValue] = useState(tenant.label ?? '');
  useEffect(() => { setValue(tenant.label ?? ''); }, [tenant.label]);
  const save = useMutation({
    mutationFn: () => setTenantLabel(tenant.id, value.trim() || null),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['my-tenants'] });
      toast.show({ type: 'success', message: value.trim() ? 'Ярлик збережено' : 'Ярлик прибрано' });
      onClose();
    },
    onError: (e) => toast.show({ type: 'error', message: extractApiError(e) }),
  });
  const changed = value.trim() !== (tenant.label ?? '');
  return (
    <div className="access-label">
      <div className="field">
        <label htmlFor={`label-${tenant.id}`}>Ваш ярлик</label>
        <input id={`label-${tenant.id}`} value={value} maxLength={60} autoFocus
          placeholder="напр. «Альфа — склад»"
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && changed) save.mutate(); }} />
      </div>
      <div className="access-note">
        Показується у фільтрі акаунтів у шапці замість назви. Бачите лише ви. Порожнє поле прибирає ярлик.
      </div>
      <div className="access-label__actions">
        <button type="button" className="primary" disabled={!changed || save.isPending}
          onClick={() => save.mutate()}>зберегти</button>
        <button type="button" onClick={onClose}>скасувати</button>
      </div>
    </div>
  );
}

/** Акаунт усередині блока: назва, роль, організації; ярлик і «перейти» — за потреби. */
function AccountGroup({ t, orgs, loading, current, withLabel, canGo, empty, onGo }: {
  t: MyTenantItem; orgs: MyOrganizationItem[]; loading: boolean; current: boolean; withLabel: boolean;
  canGo: boolean; empty: React.ReactNode; onGo: () => void;
}) {
  const [labelOpen, setLabelOpen] = useState(false);
  return (
    <div className="access-acc" data-testid={`access-${t.id}`}>
      <div className="access-acc__head">
        <div className="access-acc__title">
          <h4>{tenantDisplayName(t)}</h4>
          {t.label && <div className="access-rows__meta">Назва акаунта: {t.name}</div>}
          <div className="access-rows__meta">{t.display_role}</div>
        </div>
        <div className="access-acc__actions">
          {withLabel && (
            <button type="button" aria-expanded={labelOpen} onClick={() => setLabelOpen((o) => !o)}
              title="Своя назва акаунта для фільтра в шапці; бачите лише ви">ярлик</button>
          )}
          {canGo && (current
            ? <span className="access-rows__meta">відкрито</span>
            : <button type="button" onClick={onGo}>перейти</button>)}
        </div>
      </div>
      {labelOpen && <LabelEditor tenant={t} onClose={() => setLabelOpen(false)} />}
      {t.blocked_reason ? <Blocked reason={t.blocked_reason} />
        : loading ? LOADING
          : orgs.length === 0 ? <div className="access-note">{empty}</div>
            : <OrgRows orgs={orgs} />}
    </div>
  );
}

function ModeBlock({ mode, hint, label, current, unavailable, busy, onGo, children }: {
  mode: AccountMode; hint: string; label: string; current: boolean; unavailable: string | null;
  busy: boolean; onGo: () => void; children: React.ReactNode;
}) {
  return (
    <section className={`card access-mode${current ? ' is-current' : ''}`} data-testid={`access-mode-${mode}`}
      aria-label={`Режим «${MODE_LABEL[mode]}»`}>
      <div className="access-mode__head">
        <span className="access-mode__icon">{MODE_ICONS[mode]}</span>
        <h3>{MODE_LABEL[mode]}</h3>
        {current
          ? <span className="access-mode__here">зараз ви тут</span>
          : (
            <button type="button" disabled={busy || unavailable !== null} onClick={onGo}
              title={unavailable ?? `Перейти в режим «${MODE_LABEL[mode]}»`}>перейти</button>
          )}
      </div>
      <p className="access-mode__hint">{hint}</p>
      <div className="access-mode__body">
        <div className="access-mode__label">{label}</div>
        {children}
      </div>
    </section>
  );
}

export default function MyAccessSection() {
  const activeTenantId = useAuth((s) => s.activeTenantId);
  const toast = useToast();
  // ч.1197: перемикання клієнта — з попередженням про закриття вкладок.
  const switchWithWarning = useSwitchTenant();
  const tenantsQ = useQuery({ queryKey: ['my-tenants'], queryFn: getMyTenants });
  const orgsQ = useQuery({ queryKey: ['my-organizations'], queryFn: getMyOrganizations });
  const modeCtx = useModeContext().data;
  const effective = useEffectiveMode();
  // ч.1411: з «Облікового запису» людина лишається на сторінці.
  const setMode = useSetAccountMode();

  if (tenantsQ.isLoading) return <div className="card">Завантаження…</div>;
  const failure = tenantsQ.error ?? orgsQ.error;
  if (failure) return <div className="card"><div className="error">{extractApiError(failure)}</div></div>;

  const tenants = [...(tenantsQ.data ?? [])].sort((a, b) =>
    tenantDisplayName(a).localeCompare(tenantDisplayName(b), 'uk'));
  const own = tenants.filter((t) => t.tenant_role === 'OWNER');
  const foreign = tenants.filter((t) => t.tenant_role !== 'OWNER');
  const ownIds = new Set(own.map((t) => t.id));
  const allOrgs = [...(orgsQ.data ?? [])].sort((a, b) => a.name.localeCompare(b.name, 'uk'));
  const edoCards = allOrgs.filter((o) => ownIds.has(o.tenant_id) && o.plan === 'EDO');
  const businessOrgs = (id: string) => allOrgs.filter((o) => o.tenant_id === id && o.plan !== 'EDO');
  const ownBlocked = own.find((t) => t.blocked_reason)?.blocked_reason;
  const severalOwn = own.length > 1;
  const soleOwn = own.length === 1 ? own[0] : undefined;
  const tenantName = (id: string) => { const t = own.find((x) => x.id === id); return t ? tenantDisplayName(t) : ''; };
  const go = (t: MyTenantItem) => () => void switchWithWarning(t.id, tenantDisplayName(t));

  const block = (mode: AccountMode) => {
    const avail = modeCtx ? modeAvailable(modeCtx, mode) : { ok: false, why: undefined };
    return {
      mode, current: effective === mode, unavailable: avail.ok ? null : (avail.why ?? ''),
      busy: setMode.isPending,
      onGo: () => setMode.mutate(mode, {
        onError: (e) => toast.show({ type: 'error', message: extractApiError(e) }),
      }),
    };
  };
  const firstInMode = (what: string, mode: AccountMode, to: string, action: string) => (
    <div className="access-note">
      {what} ще немає.{' '}
      {effective === mode
        ? <Link to={to}>{action}</Link>
        : 'Щоб створити першу, перейдіть у цей режим.'}
    </div>
  );

  return (
    <div className="access-modes">
      <ModeBlock {...block('EDO')} label="Картки учасника"
        hint="Лише електронні документи: отримуєте, підписуєте і надсилаєте їх від своїх карток учасника.">
        {own.length === 0 ? <div className="access-note">Власного акаунта немає.</div>
          : orgsQ.isLoading ? LOADING
            : edoCards.length > 0 ? <OrgRows orgs={edoCards} accountOf={severalOwn ? tenantName : undefined} />
              : ownBlocked ? <Blocked reason={ownBlocked} />
                : firstInMode('Карток', 'EDO', '/edo/cards/new', 'створити картку')}
      </ModeBlock>

      <ModeBlock {...block('USER')} label="Акаунти"
        hint="Робота в чужих акаунтах, куди вас запросили: лише організації, до яких вам надали доступ.">
        {foreign.length === 0 && (
          <div className="access-note" data-testid="access-user-empty">
            Вас ще не запросили в жоден акаунт. Режим стане доступним, коли власник іншого акаунта надасть вам доступ.
          </div>
        )}
        {foreign.map((t) => (
          <AccountGroup key={t.id} t={t} orgs={allOrgs.filter((o) => o.tenant_id === t.id)}
            loading={orgsQ.isLoading} current={t.id === activeTenantId} withLabel canGo={foreign.length > 1}
            onGo={go(t)} empty="Немає доступних організацій — зверніться до власника акаунта." />
        ))}
      </ModeBlock>

      <ModeBlock {...block('OWNER')} label="Організації"
        hint="Власний бізнес: повний облік і звітність у вашому акаунті.">
        {own.length === 0 && <div className="access-note">Власного акаунта немає.</div>}
        {severalOwn && own.map((t) => (
          <AccountGroup key={t.id} t={t} orgs={businessOrgs(t.id)} loading={orgsQ.isLoading}
            current={t.id === activeTenantId} withLabel={false} canGo onGo={go(t)}
            empty="Організацій з обліком немає." />
        ))}
        {soleOwn && (
          soleOwn.blocked_reason ? <Blocked reason={soleOwn.blocked_reason} />
            : orgsQ.isLoading ? LOADING
              : businessOrgs(soleOwn.id).length > 0 ? <OrgRows orgs={businessOrgs(soleOwn.id)} />
                : firstInMode('Організацій', 'OWNER', '/organizations/new', 'створити організацію')
        )}
      </ModeBlock>
    </div>
  );
}
